import { useEffect } from 'react';
import './SlimeShotAnimation.css';

/**
 * PROFESSIONAL SLIME SHOT ANIMATION
 * Poison-type special move with sticky slime projectiles
 * 
 * Key Features:
 * - Poison-type special attack (85 base power, 90% accuracy)
 * - Fires multiple slime projectiles that stick to the target
 * - Has a 40% chance to lower the target's Speed by one stage
 * - Professional gaming company quality with fluid dynamics
 * - Special move animation with projectile physics and splatter effects
 */
export default function SlimeShotAnimation({ playerRef, opponentRef, onComplete }) {
  useEffect(() => {
    const playerEl = playerRef.current;
    const opponentEl = opponentRef.current;
    if (!playerEl || !opponentEl) return;

    // Get battle container for positioning
    const container = playerEl.closest('.battle-screen') || playerEl.closest('.battle-container') || document.body;
    const containerRect = container.getBoundingClientRect();
    
    // Calculate positions
    const playerRect = playerEl.getBoundingClientRect();
    const opponentRect = opponentEl.getBoundingClientRect();
    
    const playerX = Math.round((playerRect.left + playerRect.width / 2) - containerRect.left);
    const playerY = Math.round((playerRect.top + playerRect.height / 2) - containerRect.top);
    const opponentX = Math.round((opponentRect.left + opponentRect.width / 2) - containerRect.left);
    const opponentY = Math.round((opponentRect.top + opponentRect.height / 2) - containerRect.top);

    // Calculate trajectory
    const dx = opponentX - playerX;
    const dy = opponentY - playerY;
    const distance = Math.hypot(dx, dy);
    const angle = Math.atan2(dy, dx) * 180 / Math.PI;

    // Elements to clean up
    const elements = [];

    
    // Player charging effect
    playerEl.classList.add('slime-shot-user-charging');
    
    // Create slime pool beneath player
    const slimePool = document.createElement('div');
    slimePool.className = 'slime-shot-pool';
    slimePool.style.left = `${playerX - 60}px`;
    slimePool.style.top = `${playerY + 30}px`;
    slimePool.style.width = '120px';
    slimePool.style.height = '30px';
    container.appendChild(slimePool);
    elements.push(slimePool);

    // Create charging bubbles
    for (let i = 0; i < 10; i++) {
      const bubble = document.createElement('div');
      bubble.className = 'slime-shot-bubble';
      const bubbleX = playerX + (Math.random() - 0.5) * 80;
      const bubbleY = playerY + Math.random() * 40;
      bubble.style.left = `${bubbleX - 5}px`;
      bubble.style.top = `${bubbleY - 5}px`;
      bubble.style.animationDelay = `${i * 50}ms`;
      container.appendChild(bubble);
      elements.push(bubble);
    }

    // PHASE 2: Slime Projectiles (500-1000ms)
    const phase2Timer = setTimeout(() => {

      
      // Player shooting effect
      playerEl.classList.remove('slime-shot-user-charging');
      playerEl.classList.add('slime-shot-user-shooting');
      
      // Launch multiple slime projectiles
      const projectileCount = 5;
      for (let i = 0; i < projectileCount; i++) {
        const projectile = document.createElement('div');
        projectile.className = 'slime-shot-projectile';
        
        // Slight variation in starting position
        const offsetX = (Math.random() - 0.5) * 30;
        const offsetY = (Math.random() - 0.5) * 20;
        const startX = playerX + offsetX;
        const startY = playerY + offsetY;
        
        // Slight variation in target position
        const targetOffsetX = (Math.random() - 0.5) * 40;
        const targetOffsetY = (Math.random() - 0.5) * 30;
        const targetX = opponentX + targetOffsetX;
        const targetY = opponentY + targetOffsetY;
        
        // Calculate trajectory for this projectile
        const projectileDx = targetX - startX;
        const projectileDy = targetY - startY;
        const projectileAngle = Math.atan2(projectileDy, projectileDx) * 180 / Math.PI;
        
        // Set position and animation properties
        projectile.style.left = `${startX - 15}px`;
        projectile.style.top = `${startY - 15}px`;
        projectile.style.setProperty('--target-x', `${projectileDx}px`);
        projectile.style.setProperty('--target-y', `${projectileDy}px`);
        projectile.style.setProperty('--projectile-angle', `${projectileAngle}deg`);
        projectile.style.animationDelay = `${i * 80}ms`;
        
        container.appendChild(projectile);
        elements.push(projectile);
        
        // Add trail droplets for each projectile
        const trailTimer = setTimeout(() => {
          for (let j = 0; j < 6; j++) {
            const droplet = document.createElement('div');
            droplet.className = 'slime-shot-droplet';
            
            // Position along the trajectory
            const progress = j / 6;
            const dropletX = startX + projectileDx * progress + (Math.random() - 0.5) * 20;
            const dropletY = startY + projectileDy * progress + (Math.random() - 0.5) * 15;
            
            droplet.style.left = `${dropletX - 4}px`;
            droplet.style.top = `${dropletY - 4}px`;
            droplet.style.animationDelay = `${j * 50}ms`;
            
            container.appendChild(droplet);
            elements.push(droplet);
          }
        }, i * 80 + 100);
        elements.push({ timer: trailTimer, type: 'timer' });
      }
    }, 500);
    elements.push({ timer: phase2Timer, type: 'timer' });

    // PHASE 3: Impact and Splatter (1000-1600ms)
    const phase3Timer = setTimeout(() => {
      console.log('Slime Shot Phase 3: Impact and Splatter');
      
      // Remove shooting effect
      playerEl.classList.remove('slime-shot-user-shooting');
      
      // Opponent hit reaction
      opponentEl.classList.add('slime-shot-opponent-hit');
      
      // Create splatter effect
      const splatter = document.createElement('div');
      splatter.className = 'slime-shot-splatter';
      splatter.style.left = `${opponentX - 80}px`;
      splatter.style.top = `${opponentY - 80}px`;
      splatter.style.width = '160px';
      splatter.style.height = '160px';
      container.appendChild(splatter);
      elements.push(splatter);
      
      // Create slime drips
      for (let i = 0; i < 15; i++) {
        const drip = document.createElement('div');
        drip.className = 'slime-shot-drip';
        
        // Position around the opponent
        const dripAngle = (i / 15) * 360;
        const dripRadius = 40 + Math.random() * 50;
        const dripX = opponentX + Math.cos(dripAngle * Math.PI / 180) * dripRadius;
        const dripY = opponentY + Math.sin(dripAngle * Math.PI / 180) * dripRadius;
        
        drip.style.left = `${dripX - 6}px`;
        drip.style.top = `${dripY - 6}px`;
        drip.style.animationDelay = `${i * 40}ms`;
        drip.style.setProperty('--fall-distance', `${30 + Math.random() * 50}px`);
        
        container.appendChild(drip);
        elements.push(drip);
      }
      
      // Create speed reduction effect (40% chance)
      if (Math.random() < 0.4) {
        const speedEffect = document.createElement('div');
        speedEffect.className = 'slime-shot-speed-effect';
        speedEffect.style.left = `${opponentX}px`;
        speedEffect.style.top = `${opponentY - 50}px`;
        container.appendChild(speedEffect);
        elements.push(speedEffect);
      }
    }, 1000);
    elements.push({ timer: phase3Timer, type: 'timer' });

    // CLEANUP (1800ms)
    const cleanupTimer = setTimeout(() => {
      console.log('Slime Shot: Animation Complete - Cleanup');
      
      // Remove all CSS classes
      playerEl.classList.remove('slime-shot-user-charging', 'slime-shot-user-shooting');
      opponentEl.classList.remove('slime-shot-opponent-hit');
      
      // Remove all created elements
      elements.forEach(item => {
        if (item.type === 'timer') {
          clearTimeout(item.timer);
        } else if (item && item.parentNode) {
          item.remove();
        }
      });
      
      // Animation complete
      onComplete();
    }, 1800);

    // Cleanup function for component unmount
    return () => {
      clearTimeout(cleanupTimer);
      playerEl.classList.remove('slime-shot-user-charging', 'slime-shot-user-shooting');
      opponentEl.classList.remove('slime-shot-opponent-hit');
      elements.forEach(item => {
        if (item.type === 'timer') {
          clearTimeout(item.timer);
        } else if (item && item.parentNode) {
          item.remove();
        }
      });
    };
  }, [playerRef, opponentRef, onComplete]);

  return null;
}

// Set move type for battle system
SlimeShotAnimation.moveType = 'poison';
